// Compact, reusable utilisation bar.
//
// One-line bar + label. Used by both the Memory and Disk cells on the
// fleet overview so the visual language stays in lockstep. Thresholds
// are absolute on the percentage (used/total) — pass `warnPct` / `critPct`
// to override the defaults.

import { cn } from "@veil/ui/lib/utils";
import type { ReactNode } from "react";

interface Props {
	used: number | null | undefined;
	total: number | null | undefined;
	/** Unit suffix shown after the numeric values (e.g. "GB"). */
	unit?: string;
	/** Format a single value when both `used` and `total` are present. */
	formatValue?: (n: number) => string;
	/** Label rendered when `total` is null/undefined (graceful degradation). */
	fallbackLabel?: ReactNode;
	warnPct?: number;
	critPct?: number;
	className?: string;
	barClassName?: string;
}

const DEFAULT_WARN = 70;
const DEFAULT_CRIT = 90;

function defaultFormat(n: number): string {
	if (!Number.isFinite(n)) return "—";
	if (n >= 100) return n.toFixed(0);
	if (n >= 10) return n.toFixed(0);
	return n.toFixed(1);
}

export function UsageBar({
	used,
	total,
	unit = "GB",
	formatValue = defaultFormat,
	fallbackLabel,
	warnPct = DEFAULT_WARN,
	critPct = DEFAULT_CRIT,
	className,
	barClassName,
}: Props) {
	const hasTotal = typeof total === "number" && total > 0;
	const hasUsed = typeof used === "number" && Number.isFinite(used);

	if (!hasTotal) {
		return (
			<div className={cn("flex flex-col gap-1", className)}>
				<div className="font-mono text-xs tabular-nums text-muted-foreground">
					{fallbackLabel ??
						(hasUsed ? `${formatValue(used as number)} ${unit}` : "—")}
				</div>
			</div>
		);
	}

	const usedSafe = Math.max(0, hasUsed ? (used as number) : 0);
	const totalSafe = total as number;
	const pct = Math.min(100, Math.max(0, (usedSafe / totalSafe) * 100));
	const tone =
		pct >= critPct
			? "bg-destructive"
			: pct >= warnPct
				? "bg-amber-500"
				: "bg-emerald-500";

	return (
		<div className={cn("flex min-w-[140px] flex-col gap-1", className)}>
			<div className="flex items-baseline justify-between gap-2 font-mono text-[11px] tabular-nums text-muted-foreground">
				<span className="truncate">
					<span className="text-foreground">{formatValue(usedSafe)}</span>
					<span className="mx-0.5">/</span>
					<span>{formatValue(totalSafe)}</span>
					<span className="ml-1">{unit}</span>
				</span>
				<span className="shrink-0">{pct.toFixed(0)}%</span>
			</div>
			<div
				className={cn(
					"relative h-1 w-full overflow-hidden rounded-full bg-muted/60",
					barClassName,
				)}
				aria-hidden="true"
			>
				<div
					className={cn("absolute inset-y-0 left-0 rounded-full", tone)}
					style={{ width: `${pct}%` }}
				/>
			</div>
		</div>
	);
}
